// Pill-style segmented control used for the password/magic auth toggle and
// translation-manager filter rows. Generic over option value.

import type { ReactNode } from 'react';

import { cn } from './cn';

export interface SegmentedOption<T extends string> {
  value: T;
  label: ReactNode;
}

export interface SegmentedProps<T extends string> {
  value: T;
  options: ReadonlyArray<SegmentedOption<T>>;
  onChange: (next: T) => void;
  ariaLabel?: string;
  size?: 'sm' | 'md';
  className?: string;
}

export function Segmented<T extends string>({
  value,
  options,
  onChange,
  ariaLabel,
  size = 'md',
  className,
}: SegmentedProps<T>) {
  const padding = size === 'sm' ? 'p-0.5' : 'p-1';
  const itemPad = size === 'sm' ? 'px-3 py-1 text-xs' : 'px-3 py-1 text-sm';
  return (
    <div
      role="tablist"
      aria-label={ariaLabel}
      className={cn('inline-flex gap-1 rounded-full bg-cream-100', padding, className)}
    >
      {options.map((opt) => {
        const active = opt.value === value;
        return (
          <button
            key={opt.value}
            type="button"
            role="tab"
            aria-selected={active}
            onClick={() => onChange(opt.value)}
            className={cn(
              'rounded-full font-semibold transition-colors',
              itemPad,
              active ? 'bg-burgundy-500 text-cream-50' : 'text-burgundy-500 hover:bg-cream-200',
            )}
          >
            {opt.label}
          </button>
        );
      })}
    </div>
  );
}
